(() => {
  'use strict';

  if (window.tclTabsSectionCarouselLoaded) return;
  window.tclTabsSectionCarouselLoaded = true;

  const selector = '[data-tabs-section-carousel]';
  const instances = new Map();
  const pending = new Map();

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) roots.unshift(scope);
    return roots;
  }

  function initialize(root) {
    if (instances.has(root) || !root.isConnected) return;
    const track = root.querySelector('.wbs-carousel-track');
    const slides = track?.querySelectorAll('.wbs-carousel-slide');
    const dotsContainer = root.querySelector('.wbs-carousel-dots');
    if (!slides?.length || !dotsContainer) return;

    const duration = 5000;
    const events = new AbortController();
    let index = 0;
    let timer = null;
    let progressFrame = null;
    let touchStartX = null;
    let autoplayActive = false;
    const dots = [];
    const bars = [];

    dotsContainer.replaceChildren();
    slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.className = 'wbs-carousel-dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.dataset.index = i;
      const bar = document.createElement('div');
      bar.className = 'wbs-carousel-dot-progress';
      dot.appendChild(bar);
      dotsContainer.appendChild(dot);
      dots.push(dot);
      bars.push(bar);
    });

    function resetProgress() {
      if (progressFrame !== null) cancelAnimationFrame(progressFrame);
      progressFrame = null;
      bars.forEach((bar) => {
        bar.style.transition = 'none';
        bar.style.transform = 'scaleX(0)';
      });
    }

    function startProgress() {
      if (!autoplayActive || slides.length <= 1) return;
      resetProgress();
      // Flush all reset writes together, instead of one forced layout per dot.
      void dotsContainer.offsetWidth;
      bars[index].style.transition = `transform ${duration}ms linear`;
      progressFrame = requestAnimationFrame(() => {
        progressFrame = null;
        bars[index].style.transform = 'scaleX(1)';
      });
    }

    function updateSlide() {
      track.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }

    function stopAutoplay() {
      clearInterval(timer);
      timer = null;
      autoplayActive = false;
      resetProgress();
    }

    function startAutoplay() {
      stopAutoplay();
      if (slides.length <= 1) return;
      autoplayActive = true;
      startProgress();
      timer = setInterval(() => goTo(index + 1), duration);
    }

    function goTo(next) {
      index = (next + slides.length) % slides.length;
      updateSlide();
      startAutoplay();
    }

    function listen(element, type, handler, options = {}) {
      element?.addEventListener(type, handler, { ...options, signal: events.signal });
    }

    function click(handler) {
      return (event) => {
        event.preventDefault();
        event.stopPropagation();
        handler();
      };
    }

    listen(root.querySelector('.wbs-carousel-button.wbs-prev'), 'click', click(() => goTo(index - 1)));
    listen(root.querySelector('.wbs-carousel-button.wbs-next'), 'click', click(() => goTo(index + 1)));
    dots.forEach((dot, i) => listen(dot, 'click', click(() => goTo(i))));
    listen(root, 'mouseenter', stopAutoplay);
    listen(root, 'mouseleave', startAutoplay);
    listen(root, 'touchstart', (event) => {
      touchStartX = event.touches[0]?.clientX ?? null;
      stopAutoplay();
    }, { passive: true });
    listen(root, 'touchend', (event) => {
      const end = event.changedTouches[0]?.clientX;
      if (touchStartX !== null && end !== undefined && Math.abs(touchStartX - end) > 50) {
        goTo(index + (touchStartX > end ? 1 : -1));
      } else {
        startAutoplay();
      }
      touchStartX = null;
    }, { passive: true });
    listen(root, 'touchcancel', () => {
      touchStartX = null;
      startAutoplay();
    }, { passive: true });

    instances.set(root, () => {
      stopAutoplay();
      events.abort();
      dotsContainer.replaceChildren();
      root.removeAttribute('data-carousel-initialized');
      instances.delete(root);
    });
    root.setAttribute('data-carousel-initialized', 'true');
    updateSlide();
    startAutoplay();
  }

  function schedule(scope) {
    rootsWithin(scope).forEach((root) => {
      if (instances.has(root) || pending.has(root)) return;
      // Keep the existing post-DOM initialization delay.
      pending.set(root, setTimeout(() => {
        pending.delete(root);
        initialize(root);
      }, 500));
    });
  }

  function unload(scope) {
    rootsWithin(scope).forEach((root) => {
      if (pending.has(root)) {
        clearTimeout(pending.get(root));
        pending.delete(root);
      }
      instances.get(root)?.();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(document), { once: true });
  } else {
    schedule(document);
  }
  document.addEventListener('shopify:section:load', (event) => schedule(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
